import type * as React from 'react'
import {
  createContext,
  useCallback,
  useContext,
  useId,
  useMemo,
  useState,
} from 'react'

import { cn } from '@/lib/utils'

/**
 * Dependency-free tabs (the app doesn't ship @radix-ui/react-tabs). Supports
 * controlled (`value`/`onValueChange`) and uncontrolled (`defaultValue`) use,
 * mirroring the shadcn Tabs API surface the dashboard needs.
 */
interface TabsContextValue {
  value: string | undefined
  setValue: (value: string) => void
  baseId: string
}

const TabsContext = createContext<TabsContextValue | null>(null)

function useTabs() {
  const ctx = useContext(TabsContext)
  if (!ctx) throw new Error('Tabs components must be used within <Tabs>')
  return ctx
}

function Tabs({
  className,
  value: controlled,
  defaultValue,
  onValueChange,
  children,
  ...props
}: Omit<React.ComponentProps<'div'>, 'onChange'> & {
  value?: string
  defaultValue?: string
  onValueChange?: (value: string) => void
}) {
  const baseId = useId()
  const [uncontrolled, setUncontrolled] = useState(defaultValue)
  const value = controlled ?? uncontrolled

  const setValue = useCallback(
    (next: string) => {
      if (controlled === undefined) setUncontrolled(next)
      onValueChange?.(next)
    },
    [controlled, onValueChange],
  )

  const ctx = useMemo(
    () => ({ value, setValue, baseId }),
    [value, setValue, baseId],
  )

  return (
    <TabsContext.Provider value={ctx}>
      <div
        data-slot="tabs"
        className={cn('flex flex-col gap-2', className)}
        {...props}
      >
        {children}
      </div>
    </TabsContext.Provider>
  )
}

function TabsList({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      role="tablist"
      data-slot="tabs-list"
      className={cn(
        'bg-muted text-muted-foreground inline-flex h-9 w-fit items-center justify-center rounded-lg p-[3px]',
        className,
      )}
      {...props}
    />
  )
}

function TabsTrigger({
  className,
  value,
  ...props
}: React.ComponentProps<'button'> & { value: string }) {
  const { value: active, setValue, baseId } = useTabs()
  const selected = active === value
  return (
    <button
      type="button"
      role="tab"
      id={`${baseId}-trigger-${value}`}
      aria-selected={selected}
      aria-controls={`${baseId}-content-${value}`}
      data-slot="tabs-trigger"
      data-state={selected ? 'active' : 'inactive'}
      onClick={() => setValue(value)}
      className={cn(
        "inline-flex h-[calc(100%-1px)] flex-1 items-center justify-center gap-1.5 rounded-md border border-transparent px-2 py-1 text-sm font-medium whitespace-nowrap transition-[color,box-shadow] outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4",
        selected
          ? 'bg-background text-foreground shadow-sm dark:border-input dark:bg-input/30'
          : 'text-muted-foreground hover:text-foreground',
        className,
      )}
      {...props}
    />
  )
}

function TabsContent({
  className,
  value,
  ...props
}: React.ComponentProps<'div'> & { value: string }) {
  const { value: active, baseId } = useTabs()
  const selected = active === value
  return (
    <div
      role="tabpanel"
      id={`${baseId}-content-${value}`}
      aria-labelledby={`${baseId}-trigger-${value}`}
      hidden={!selected}
      data-slot="tabs-content"
      data-state={selected ? 'active' : 'inactive'}
      className={cn('flex-1 outline-none', !selected && 'hidden', className)}
      {...props}
    />
  )
}

export { Tabs, TabsContent, TabsList, TabsTrigger }
